import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const navSections = ['experience', 'skills', 'projects'] as const;

export function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const currentLang = i18n.language === 'en' ? 'en' : 'es';
  const otherLang = currentLang === 'es' ? 'en' : 'es';
  const isHome = location.pathname === `/${currentLang}`;

  const toggleLanguage = () => {
    i18n.changeLanguage(otherLang);
    const newPath = location.pathname.replace(`/${currentLang}`, `/${otherLang}`);
    navigate(newPath || `/${otherLang}`);
  };

  const handleSectionClick = (e: React.MouseEvent, section: string) => {
    e.preventDefault();
    setMenuOpen(false);
    if (isHome) {
      document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/${currentLang}`);
      setTimeout(() => {
        document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-bg-primary/90 backdrop-blur-sm">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <Link
          to={`/${currentLang}`}
          className="font-mono text-sm text-accent"
          onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setMenuOpen(false); }}
        >
          DG_
        </Link>

        <div className="hidden items-center gap-6 md:flex">
          {navSections.map((section, i) => (
            <div key={section} className="flex items-center gap-6">
              {i > 0 && <span className="h-3 w-px bg-accent/20" />}
              <a
                href={`/${currentLang}#${section}`}
                onClick={(e) => handleSectionClick(e, section)}
                className="text-sm text-text-muted transition-colors hover:text-text-primary"
              >
                {t(`nav.${section}`)}
              </a>
            </div>
          ))}
          <span className="h-3 w-px bg-accent/20" />
          <Link
            to={`/${currentLang}/how-i-was-built`}
            className="border border-accent px-3 py-1 font-mono text-xs text-accent transition-colors hover:bg-accent hover:text-bg-primary"
          >
            {t('nav.howBuilt')}
          </Link>
          <span className="h-3 w-px bg-accent/20" />
          <a
            href={`/${currentLang}#contact`}
            onClick={(e) => handleSectionClick(e, 'contact')}
            className="text-sm text-text-muted transition-colors hover:text-text-primary"
          >
            {t('nav.contact')}
          </a>
        </div>

        <div className="flex items-center gap-3 md:gap-0">
          <button
            onClick={toggleLanguage}
            className="border border-border px-3 py-1 font-mono text-xs text-text-muted transition-colors hover:border-accent hover:text-accent"
          >
            {otherLang.toUpperCase()}
          </button>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex flex-col gap-1 p-1 md:hidden"
            aria-label="Toggle menu"
          >
            <span className={`block h-px w-5 bg-accent transition-all ${menuOpen ? 'translate-y-[5px] rotate-45' : ''}`} />
            <span className={`block h-px w-5 bg-accent transition-all ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block h-px w-5 bg-accent transition-all ${menuOpen ? '-translate-y-[5px] -rotate-45' : ''}`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-border bg-bg-primary/95 backdrop-blur-sm md:hidden">
          <div className="flex flex-col px-6 py-4 gap-4">
            {navSections.map((section) => (
              <a
                key={section}
                href={`/${currentLang}#${section}`}
                onClick={(e) => handleSectionClick(e, section)}
                className="text-sm text-text-muted transition-colors hover:text-text-primary"
              >
                {t(`nav.${section}`)}
              </a>
            ))}
            <Link
              to={`/${currentLang}/how-i-was-built`}
              onClick={() => setMenuOpen(false)}
              className="text-sm text-accent transition-colors hover:text-text-primary"
            >
              {t('nav.howBuilt')}
            </Link>
            <a
              href={`/${currentLang}#contact`}
              onClick={(e) => handleSectionClick(e, 'contact')}
              className="text-sm text-text-muted transition-colors hover:text-text-primary"
            >
              {t('nav.contact')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
